import { apiClient } from './client';

/**
 * 음성 녹음 세션 — **웹이 이것을 읽기 시작하는 첫 모듈**이다 (2026-10-04 · F1-c).
 *
 * ## ⛔ 왜 `minutes.ts` 에 안 넣었나 — **다른 서비스다**
 *
 * ```
 * 회의록   /minutes/api/v1   → minutes-svc(:4103)   ← minutes.ts 의 bare axios(minutesClient)
 * 음성세션 /api/v1/voice     → chat-server(:3000)   ← ★ 이 모듈 · apiClient
 * ```
 * ⇒ ★ 그래서 **합치는 일은 «화면»에서 한다** — 서버가 둘을 이어 주는 자리는 없다.
 *   (`awaiting-summary.ts` 가 그 합치는 규칙을 들고 있고, 제 시험이 그것을 잠근다.)
 *
 * ## ⚠ 종전 주석 둘이 「0건」이라 적어 두었다 — 이 파일이 그것을 **바꾼다**
 *
 * `AiMinutesPage.tsx` 와 `AiMinutesPage.origin.test.tsx` 가 「실측: `voice/sessions` 를
 * 부르는 곳 0건」이라 적어 두었다. ⇒ ★ 이제 **1건**이다(이 모듈). 그 주석들도 같은 판에서 고친다.
 */

/**
 * 목록 한 줄 — ⛔ **서버가 주는 칸을 «그대로»** 적는다
 * (`apps/chat-server/src/modules/voice/voice.routes.ts:473` 의 응답 · 2026-10-04 실측).
 *
 * ★ 이름을 바꾸지 않는다(snake_case 유지) — 바꾸면 서버 응답과 화면이 **두 벌**이 되고,
 *   서버가 칸을 더한 날 어느 쪽이 진실인지 알 수 없다.
 */
export interface VoiceSessionItem {
  session_id: string;
  /**
   * ⛔ 이 칸으로 **「정리 대기」를 가르지 않는다.**
   *
   * 까닭(2026-10-04 로컬 실측): 「정리 대기」를 뜻하는 `awaiting_summary` 는 마이그 221 이
   * 만들었지만 **되메우기(UPDATE)가 없어** 그 상태인 행이 **0건**이다 — 멈춰 있는 17건이
   * 전부 `awaiting_end` 다. ⇒ ★ 판정은 `minutes_id === null` 로 한다
   * (`awaiting-summary.ts` 의 `isAwaitingSummary`).
   */
  status: string;
  title: string;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
  /** ⚠ 끝이 시작보다 «앞»이면 서버가 **null** 을 준다 — 0 을 쓰지 않는다(「0초 녹음」은 거짓). */
  duration_ms: number | null;
  /**
   * ⛔⛔ **`null` 일 수 있다** — 그리고 그것은 **우연이 아니라 «구조»다** (2026-10-06 · 관제가 잡음).
   *
   * ```
   * 서버는 이 칸을 ★ «녹음을 끝낼 때» 채운다 — voice.repo.ts:168
   *   SET chunk_count = COALESCE(chunk_count, $n),
   *       status = CASE WHEN status='collecting' THEN 'awaiting_end' ELSE status END
   * ⇒ ★ 즉 `collecting` 인 동안은 ⭐ 구조적으로 NULL 이다
   * ⇒ 서버도 `number | null` 로 내보낸다 — repo 의 그 칸 선언 **다섯 자리** ＋ routes 의 직렬화
   *   (`chunk_count: r.chunk_count`) ⇒ ⚠ **줄 번호는 움직이므로 그 «글자»로 찾을 것**
   * ⇒ ⭐ 그리고 repo 가 그 뜻을 적어 두었다 — 「`chunk_count IS NOT NULL` = 종료 마커가 왔다
   *   (안 왔으면 아직 «전사중»)」 (2026-10-06 현재 :897)
   * 로컬 실측(2026-10-06): collecting 2건이 ★ 둘 다 chunk_count NULL · minutes_id NULL
   * ```
   * ⚠⚠ **한때 이 타입이 `number` 였다** — 그 **거짓 타입**이 `received_chunks < chunk_count` 를
   *   TS 에게 「안전하다」고 통과시켰다. ⇒ ⛔ JS 는 `1 < null` 을 **`1 < 0`** 으로 보므로 거짓이 되고,
   *   녹음이 **도는 중**인 세션이 「전사가 끝났지만 글자가 없습니다」를 받았다.
   * ⇒ ⇒ ★ 사보타주 **열하나(A~K)가 그 길을 한 번도 지나가지 않았다** — 흉내에 그런 세션이 없었고,
   *   **타입이 그것을 못 만들게 막고 있었기** 때문이다. ⇒ ⭐ 「거짓 타입은 시험도 눈을 가린다」.
   */
  chunk_count: number | null;
  received_chunks: number;
  /**
   * ⛔⛔ **「요약할 글이 있나」로 쓸 수 «없다».**
   *
   * `stt_status='done'` 인데 **글자가 빈** 조각이 **정상 결과**다(조용한 5분 · STT 문턱) —
   * `voice-worker.ts:292` 가 「→ done + 빈 transcript」라 적는다. 2026-10-04 로컬 실측:
   * `done` 조각 24건 중 글자 있는 것 **10** · 빈 것 **14**. 세션으로 보면 「정리 대기」 17건이
   * **전부** 이 수가 0 보다 큰데 **글자가 있는 것은 5건**이다.
   * ⇒ ✅ **2026-10-05 — 그래서 서버가 `transcript_chars` 를 내준다**(㉤ · PR #2178).
   *   아래 그 칸을 쓴다. ⇒ ⛔ 이 칸(`transcribed_chunks`)으로 「글이 있나」를 **판정하지 않는다.**
   */
  transcribed_chunks: number;
  /**
   * ★★ **전사된 글자 수** — 「전사됐다」와 **「글이 있다」를 가르는** 칸 (㉤ · PR #2178 · 2026-10-05).
   *
   * ```
   * 서버가 세는 꼴: coalesce(sum(length(coalesce(btrim(transcript), ''))), 0)::bigint
   *   ⇒ ★ 공백만인 전사는 ⭐ 0 · 조각이 아예 없으면 ★ null 이 아니라 ⭐ 0
   * 자리: 목록(`:519`) ＋ 상세(`:578`) ⇒ ★ «같은 이름»(서버 repo 머리말의 그 규율)
   * ```
   * ⛔ **서버는 세기만 하고 «판정하지 않는다»**(서버 주석 `:515`의 그 줄) — 기준이 바뀌면 서버를
   *   또 고쳐야 하기 때문이다. ⇒ ★ 그래서 `> 0` 판정은 **화면이** 하고, 그 판정은
   *   `pages/AiMinutesPage/awaiting-summary.ts` 의 `transcriptTextState` **한 곳**에 있다.
   *
   * ⚠ **`?`(optional)인 까닭** — 이 칸은 2026-10-05 에 생겼다. ⇒ ⛔ 그보다 낡은 서버는 **안 준다**
   *   ⇒ ★ 그때 `0` 으로 읽으면 화면이 **「요약할 글이 없습니다」라는 거짓말**을 한다.
   *   ⇒ ⇒ ⭐ 그래서 **「모른다」와 「0」을 가른다** — `transcriptTextState` 가 `'unknown'` 을 낸다.
   */
  transcript_chars?: number;
  /** 「전사 멈춤」을 목록에서 볼 **유일한** 칸(서버 주석 `:509`). */
  failed_chunks: number;
  /**
   * ⭐ 2026-10-20 (시안 ② · 서버 #2553) — 실패 조각 중 다시 받아 적기를 «다 써» 요약이 건너뛸 수.
   *   「다시 받아 적는 중」 = failed_chunks − gave_up_chunks. ⚠ 옛 서버는 칸이 «없다» ⇒ 종전 판정(멈춘다).
   */
  gave_up_chunks?: number;
  /**
   * ★★ **아직 «말단이 아닌» 조각 수** — 「기다리면 되나」를 가르는 칸 (㉥ · PR #2190 · 2026-10-06).
   *
   * ```
   * 서버가 세는 꼴: count(*) FILTER (WHERE stt_status NOT IN ('done','failed','device_side'))
   *   ⇒ ★ 말단 셋을 «빼고» 센다 ⇒ ⭐ pending · running · skipped 만 남는다
   * 자리: 목록 ＋ 상세 ⇒ ★ «같은 이름»(서버가 그 규율을 지켰다 — 관제가 확인)
   * ```
   * ⇒ ⭐ 이 칸이 서기 «전»에는 화면이 `received − done − failed` 로 셌고, 그 셈에는
   *   **`device_side`(말단)가 섞여** 「말단인 조각을 아직 도는 중」이라 말할 수 있었다.
   *   ⇒ ★ 이 칸이 그 **좁음을 없앤다**.
   *
   * ⚠⚠ **`?`(optional)인 까닭 — `chunk_count` 에서 겪은 그 함정과 «같은 꼴»이다.**
   *   옛 서버는 이 칸을 **안 준다** ⇒ `undefined` ⇒ ⛔ `undefined > 0` 은 **늘 false** 라
   *   **조용히 `settled` 로 샌다**(「전사가 끝났다」는 거짓말). ⇒ ★ 그래서 타입을 **정직하게** 쓰고
   *   `typeof !== 'number'` 갈래를 **따로** 둔다(`noTextReason` 의 그 자리).
   */
  pending_chunks?: number;
  summarized_count: number;
  audio_ready: boolean;
  /**
   * ⭐ 2026-10-20 (시안 ① · 서버 #2554 · 마이그 232) — 녹음 합본이 실패했나(회의록은 서도 내려받을 합본이 없다).
   *   ⚠ 옛 서버는 칸이 «없다» ⇒ 종전처럼 status === 'failed' 로 읽는다.
   */
  merge_failed?: boolean;
  audio_file_id: string | null;
  /** ★★ **합치는 열쇠** — null 이면 회의록이 아직 없다(= 화면 「정리 대기」). */
  minutes_id: string | null;
  favorite: boolean;
}

export interface VoiceSessionListResult {
  items: VoiceSessionItem[];
  nextCursor: string | null;
  /** 기관에 STT 엔진이 붙어 있나. */
  stt_available: boolean;
  /** 요약 엔진이 살아 있나 — ⚠ false 면 눌러도 워커가 **미룬다**(실패가 아니다). */
  summary_available: boolean;
}

/** 서버 상한 = `VOICE_LIST_MAX_LIMIT`(voice.repo.ts:247). 더 크게 보내도 서버가 깎는다. */
export const VOICE_SESSION_MAX_LIMIT = 100;

/**
 * 내 음성 세션 목록.
 *
 * ★ **「제 것」만 온다** — `listOwnedSessions` 의 WHERE 가
 * `institution_id = 기관 AND owner_user_id = 나 AND status <> 'purged'` 다(voice.repo.ts:293).
 * ⇒ 합친 목록이 **남의 녹음을 보일 일은 없고**, 파기된 것은 **애초에 빠진다.**
 */
export async function listVoiceSessions(params?: {
  limit?: number;
  cursor?: string;
}): Promise<VoiceSessionListResult> {
  const r = await apiClient.get<VoiceSessionListResult>('/voice/sessions', {
    params: { limit: params?.limit ?? VOICE_SESSION_MAX_LIMIT, cursor: params?.cursor },
  });
  // 방어 가드 — items 가 배열이 아닌 비정형 응답에도 소비처 map 이 안 깨지게(minutes.ts 선례).
  return {
    items: Array.isArray(r.data?.items) ? r.data.items : [],
    nextCursor: r.data?.nextCursor ?? null,
    stt_available: r.data?.stt_available ?? false,
    summary_available: r.data?.summary_available ?? false,
  };
}

export interface SummarizeAck {
  session_id: string;
  /**
   * ⛔ **「글이 없다」가 아니다.** `false` 는 「이미 큐에 그 잡이 있다」는 뜻이다
   * (부분 유니크 인덱스가 중복을 흡수한다 · `voice.routes.ts:337`).
   * ⇒ ★ 화면은 둘을 **같게** 다룬다 — 어느 쪽이든 「요약을 걸었다」다.
   */
  queued: boolean;
}

/**
 * 손으로 요약을 건다 — **본문 없이** 부른다.
 *
 * ⛔ 서버에 **본문 스키마가 없다**(의도적 — 「칸이 없는데 `type:'object'` 를 두면 `undefined`
 * 를 재서 400 이 된다」, `lib/absent-body.ts` 머리말). ⇒ ★ 그래서 두 번째 인수를 **안 보낸다.**
 *
 * ⚠ **202 는 「요약이 됐다」가 아니다** — 잡을 걸었다는 뜻이고, 처리는 워커가 뒤에서 한다.
 *   그리고 전사 글자가 없으면 워커는 AI 를 **안 부르고** 조용히 물높이만 올린다
 *   (`voice-worker.ts` `handleSummarize` 의 `text.trim() === ''` 갈래).
 *   ⇒ ★ 그 조용함을 화면이 말해 주려면 서버 요청 ㉤ 가 서야 한다.
 */
/**
 * 조각 한 벌의 ★ **전사 한 줄** — ⇒ ⭐ 「전체」 탭이 ★ 시간별로 그린다.
 *
 * ⚠ `status` 여섯의 ★ 뜻이 ⭐ 다르고, ⛔ **셋은 «실패가 아니다»**. 서버 머리말이 적고 있다
 *   (`voice.routes.ts` 의 `GET …/transcript`):
 * ```
 *   done        전사가 끝났다 — ⚠ 글이 ★ «빈» 수도 있다(⇒ ⭐ 「조용한 5분」 · ⛔ 실패가 아니다)
 *   device_side ⭐ 단말이 전사했다 ⇒ ★ 서버에 글이 «없다»(text 가 '') · ⛔ 실패가 아니다
 *   skipped     ⭐ «말단»이다 — ★ 되살릴 것도 없다(`CHUNK_STT_STATUSES` 머리말)
 *   pending     아직 줄 서 있다          ⛔ running 과 ★ «합치지 않는다» — 사용자에게 다른 말이다
 *   running     지금 돌고 있다
 *   failed      전사가 ★ 끝내 실패했다
 * ```
 */
export type ChunkSttStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped' | 'device_side';

export interface TranscriptChunk {
  seq: number;
  /** 녹음 ★ 시작부터의 ⭐ 밀리초 — ⇒ 「시간별」의 그 시각이다. */
  start_ms: number;
  /** ⚠ 서버가 `null` 을 ★ 안 내린다 — ⭐ 없으면 ''. ⇒ 갈래가 필요하면 `status` 를 본다. */
  text: string;
  status: ChunkSttStatus;
}

/**
 * ⚠ 세 낱말이 ★ «다른 것»을 센다 — ⇒ ⛔ 섞으면 판정이 틀린다(서버 머리말 그대로):
 * ```
 *   chunk_count      ★ «올라올 예정»인 수 — ⇒ ⚠ 종료 마커가 채운다 ⇒ ⭐ 그전엔 `null`
 *                    ⛔ `0` 으로 읽으면 안 된다 — 「0개 예정」이 아니라 ★ 「몇 개인지 ⭐ 모른다」다
 *   progress.total   ★ «받은» 행 수
 *   progress.joined  ★ `text` 에 «이어 붙은» 수 (⇒ 구멍에서 멈춘다)
 * ```
 * ⇒ ⭐ 그래서 ★ 「전사가 다 됐나」는 ⇒ `transcriptComplete()` ★ 한 곳에 둔다(아래).
 */
export interface VoiceTranscript {
  session_id: string;
  status: string;
  chunk_count: number | null;
  /** ⭐ 구멍에서 «멈춘» 이어 붙인 글 — ⛔ 구멍을 건너뛰고 잇지 «않는다»(거짓이 된다). */
  text: string;
  chunks: TranscriptChunk[];
  progress: {
    total: number; joined: number; done: number; device_side: number;
    pending: number; running: number; failed: number; skipped: number;
  };
}

/**
 * ★★★ 「전사가 ⭐ 다 됐나」 — ⇒ ⚠ 판정을 ★ 한 곳에 둔다.
 *
 * ⛔ `joined >= total` «만» 보면 ⇒ ★ ⭐ «꼬리가 안 올라온» 세션을 ★ 「다 됐다」로 읽는다 —
 *   서버 머리말이 그 사고를 적고 있다: 「5조각짜리인데 ★ 2개만 올라옴 ⇒ ⭐ total 2 · joined 2
 *   ⇒ ⇒ 「받은 것 «안»에 구멍 없음」이 ★ 참 ⇒ ⇒ ⛔ 앱이 「전사 다 됐다」로 읽는다 ⇒ ★ 반쪽 회의록」.
 * ⇒ ⇒ ⚠ 그리고 ★ 그 쪽이 ⭐ «더 나쁘다» — 「덜 됐다」로 기우는 다른 둘과 달리
 *   ⇒ ★ 이것은 ⭐ 「다 됐다」로 기울어 ⇒ ★ 사용자가 ⭐ «반쪽인 줄 모른다».
 * ⇒ ✅ 그래서 ★ `chunk_count` 가 ⭐ `null` 이면 ★ **거짓**이다(「아직 안 끝났다」).
 */
export function transcriptComplete(t: Pick<VoiceTranscript, 'chunk_count' | 'progress'>): boolean {
  return t.chunk_count !== null
    && t.progress.total >= t.chunk_count
    && t.progress.joined >= t.progress.total;
}

/**
 * 그 세션의 전사를 읽는다.
 *
 * ⚠ 권한은 ★ ⭐ «작성자만»이다 — 서버가 `getOwnedSession` 으로 보고 ⇒ ⛔ 남의 것은 **404** 다
 *   (그 주석: 「소유 검사는 ★ 기존 잣대 그대로 — ⭐ 본인 것만 … 원문은 회의록보다 ★ 날것이라
 *   ⭐ 더 넓힐 까닭이 없다」). ⇒ ★ 그래서 ⭐ 404 를 «오류로 떠들지 않는다» — `null` 을 돌려주고
 *   ⇒ 화면이 ★ 「없다」로 그린다.
 */
export async function getVoiceTranscript(sessionId: string): Promise<VoiceTranscript | null> {
  try {
    const r = await apiClient.get<VoiceTranscript>(`/voice/sessions/${sessionId}/transcript`);
    return r.data ?? null;
  } catch {
    // ⛔ 404(남의 것·없는 것)와 그 밖을 ★ 가르지 않는다 — ⇒ 화면이 할 수 있는 일이 ⭐ 같다.
    return null;
  }
}
export async function summarizeVoiceSession(sessionId: string): Promise<SummarizeAck> {
  const r = await apiClient.post<SummarizeAck>(`/voice/sessions/${sessionId}/summarize`);
  return { session_id: r.data?.session_id ?? sessionId, queued: r.data?.queued ?? false };
}
